import React, { useState } from 'react';
import { formatUSD, todayISO, COMPANY } from '../utils/keyGenerator';
import { downloadStatementPDF } from '../utils/pdfUtils';
import TxTable from '../components/TxTable';

export default function Reports({ transactions, wallet, totalCredit, totalDebit }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState(todayISO());

  const filtered = transactions.filter(tx => {
    const d = tx.date?.slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });

  const filteredCredit = filtered.reduce((s, t) => s + (t.creditAmount || 0), 0);
  const filteredDebit = filtered.reduce((s, t) => s + (t.debitAmount || 0), 0);

  const exportCSV = () => {
    const headers = ["Reference", "Date", "Description", "Counterparty", "Type", "Credit(USD)", "Debit(USD)", "Status"];
    const rows = filtered.map(tx => [
      tx.id || tx.ref,
      tx.date?.slice(0, 10),
      tx.description || "",
      tx.counterparty || "",
      tx.mt || tx.type,
      tx.creditAmount || "",
      tx.debitAmount || "",
      tx.status,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `BOS_Ledger_${todayISO()}.csv`;
    a.click();
  };

  const exportPDF = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>BOS Ledger Report</title>
    <style>
      body { font-family: 'Segoe UI', sans-serif; font-size: 12px; padding: 24px; color: #1E293B; }
      table { width: 100%; border-collapse: collapse; margin-top: 16px; }
      td, th { border: 1px solid #E2E6F0; padding: 6px 10px; text-align: left; }
      th { background: #F4F6FB; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #64748B; }
      .header { text-align: center; border-bottom: 2px solid #1B2A4A; margin-bottom: 24px; padding-bottom: 16px; }
      .header h2 { margin: 0; font-size: 20px; color: #1B2A4A; }
      .header p { margin: 4px 0 0; color: #64748B; }
      .summary { margin-top: 20px; border-top: 2px solid #1B2A4A; padding-top: 12px; display: flex; gap: 24px; }
      .summary strong { color: #1B2A4A; }
      .credit { color: #0D9488; } .debit { color: #E74C6F; }
    </style>
    </head><body>
    <div class="header">
      <h2>${COMPANY}</h2>
      <p>Transaction Ledger Report &mdash; ${todayISO()}</p>
    </div>
    <table>
      <thead><tr>
        <th>Reference</th><th>Date</th><th>Description</th><th>Counterparty</th><th>Type</th><th>Credit</th><th>Debit</th><th>Status</th>
      </tr></thead>
      <tbody>
        ${filtered.map(tx => `<tr>
          <td style="font-family:monospace;font-size:10px">${tx.id || ""}</td>
          <td>${tx.date?.slice(0, 10) || ""}</td>
          <td>${tx.description || ""}</td>
          <td>${tx.counterparty || ""}</td>
          <td>${tx.mt || tx.type}</td>
          <td class="credit">${tx.creditAmount ? formatUSD(tx.creditAmount) : ""}</td>
          <td class="debit">${tx.debitAmount ? formatUSD(tx.debitAmount) : ""}</td>
          <td>${tx.status}</td>
        </tr>`).join("")}
      </tbody>
    </table>
    <div class="summary">
      <strong>Total Credit: <span class="credit">${formatUSD(totalCredit)}</span></strong>
      <strong>Total Debit: <span class="debit">${formatUSD(totalDebit)}</span></strong>
      <strong>Net Balance: ${formatUSD(wallet.balance)}</strong>
    </div>
    </body></html>`);
    w.print();
  };

  return (
    <div className="gap-16">
      <div className="card">
        <div className="card-header">
          <span className="card-title">Report Summary</span>
          <div className="btn-row" style={{ margin: 0 }}>
            <button className="btn btn-teal btn-sm" onClick={exportCSV}>Export CSV</button>
            <button className="btn btn-gold btn-sm" onClick={() => downloadStatementPDF(filtered, wallet, { from: dateFrom, to: dateTo, title: 'Statement of Transactions' })}>Download PDF</button>
            <button className="btn btn-outline btn-sm" onClick={exportPDF}>Print PDF</button>
          </div>
        </div>
        <div className="card-body">
          {/* Date Filters */}
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="form-label">Date From</label>
              <input className="form-input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="form-label">Date To</label>
              <input className="form-input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>

          {/* Stats */}
          <div className="grid3" style={{ marginBottom: 24 }}>
            {[
              { label: "Filtered Transactions", value: filtered.length, cls: "", tile: "blue" },
              { label: "Total Credit", value: formatUSD(filteredCredit), cls: "stat-green", tile: "teal" },
              { label: "Total Debit", value: formatUSD(filteredDebit), cls: "stat-red", tile: "coral" },
            ].map((s, i) => (
              <div key={i} className={`stat-tile ${s.tile}`}>
                <div className="stat-label">{s.label}</div>
                <div className={`stat-value ${s.cls}`} style={{ fontSize: 22 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Transaction Table */}
          <TxTable rows={filtered} />
        </div>
      </div>
    </div>
  );
}
